import { animated, useTransition } from '@react-spring/web';
import { useQuery } from '@tanstack/react-query';
import { differenceInCalendarDays } from 'date-fns';
import { FormikProvider, useFormik, type FormikErrors } from 'formik';
import { useEffect, useState, type ReactNode } from 'react';
import styled from 'styled-components';

import { Button } from '../../cdl/Button/Button';
import { Callout } from '../../cdl/Callout/Callout';
import { ProgressSteps } from '../../cdl/ProgressSteps/ProgressSteps';
import { Text } from '../../cdl/Text/Text';
import { useToasts } from '../../cdl/Toast/useToasts';
import { queries } from '../../common/api/queryKeys/queries';
import { ContentLoadingOverlay } from '../../common/ContentLoadingOverlay/ContentLoadingOverlay';
import { AdvancedError } from '../../common/Error/AdvancedError';
import { translate } from '../../common/helpers/translate.helper';
import { useRole } from '../../common/hooks/useRole';
import { LoadingIndicator } from '../../common/LoadingIndicator/LoadingIndicator';
import { Box } from '../../common/ui/Box';
import { theme } from '../../common/ui/theme';
import { getCalculatedDemandScope } from '../getCalculatedDemandScope';
import { getFleetChangeError } from '../getFleetChangeError';
import { useCreateDemandDefinition } from '../hooks/useCreateDemandDefinition';
import { useDemandCalculation } from '../hooks/useDemandCalculation';
import { useDemandCalculationExport } from '../hooks/useDemandCalculationExport';
import { useDemandDefinitionNavigationGuard } from '../hooks/useDemandDefinitionNavigationGuard';
import { useDemandDefinitionProgress } from '../hooks/useDemandDefinitionProgress';
import { usePatchDemandDefinition } from '../hooks/usePatchDemandDefinition';
import { useUpdateDemandDefinition } from '../hooks/useUpdateDemandDefinition';
import { toDemandDefinitionRequestInput } from '../toDemandDefinitionRequestInput';
import type { DemandCalculation } from '../types/DemandCalculation';
import { DemandDataBasis } from '../types/DemandDataBasis';
import type { DemandDefinition } from '../types/DemandDefinition';
import type { DemandDefinitionState } from '../types/DemandDefinitionState';
import { DemandDefinitionSubstep } from '../types/DemandDefinitionSubstep';
import { DemandProductCategory } from '../types/DemandProductCategory';
import type { DemandProductCategoryOption } from '../types/DemandProductCategoryOption';

import { DemandCalculationErrorOverlay } from './DemandCalculationErrorOverlay';
import { DemandDataFreezeControl } from './DemandDataFreezeControl';
import { DemandDefinitionCalculationContext } from './DemandDefinitionCalculationContext';
import { DemandDefinitionCustomerGroupField } from './DemandDefinitionCustomerGroupField';
import { DemandDefinitionFlowActions } from './DemandDefinitionFlowActions';
import { DemandDefinitionFlowLayout } from './DemandDefinitionFlowLayout';
import { DemandDefinitionSummaryPanel } from './DemandDefinitionSummaryPanel';
import { DemandDraftSaveErrorOverlay } from './DemandDraftSaveErrorOverlay';
import { ExpectedFleetChangesStep } from './steps/ExpectedFleetChangesStep';
import { FleetChangesStep } from './steps/FleetChangesStep';
import { FleetScopeStep } from './steps/FleetScopeStep';
import { GeneralStep } from './steps/GeneralStep';
import { HistoricalDemandStep } from './steps/HistoricalDemandStep';
import { ProductCategoriesStep } from './steps/ProductCategoriesStep';
import { ReferencePeriodStep } from './steps/ReferencePeriodStep';

const StepContent = styled.div<{ $stretchContent: boolean }>`
    box-sizing: border-box;
    display: ${({ $stretchContent }) => ($stretchContent ? 'flex' : 'block')};
    flex-direction: column;
    flex: 1;
    width: 100%;
    min-width: 630px;
    max-width: 630px;
    margin: 0 auto;
    padding: ${theme.space[48]} ${theme.space[40]};
`;

enum DraftSaveIntent {
    STAY_IN_FLOW = 'stayInFlow',
    CLOSE_FLOW = 'closeFlow',
}

enum DemandFlowOverlay {
    CALCULATION_ERROR = 'calculationError',
    DRAFT_SAVE_ERROR = 'draftSaveError',
}

const getSubstepCopy = (substep: DemandDefinitionSubstep) => {
    switch (substep) {
        case DemandDefinitionSubstep.GENERAL:
            return {
                title: translate('demandDefinition.details'),
                description: translate('demandDefinition.createReusableDemandDefinitionDescription'),
            };
        case DemandDefinitionSubstep.REFERENCE_PERIOD:
            return {
                title: translate('demandDefinition.referencePeriod'),
                description: translate('demandDefinition.selectHistoricalPeriodDescription'),
            };
        case DemandDefinitionSubstep.FLEET_SCOPE:
            return {
                title: translate('demandDefinition.fleetScope'),
                description: translate('demandDefinition.selectCompaniesForDemandCalculationDescription'),
            };
        case DemandDefinitionSubstep.PRODUCT_CATEGORIES:
            return {
                title: translate('demandDefinition.productCategories'),
                description: translate('demandDefinition.productCategoriesSelectionDescription'),
            };
        case DemandDefinitionSubstep.NEWLY_ADDED_VESSELS:
            return {
                title: translate('demandDefinition.addedVessels'),
                description: translate(
                    'demandDefinition.chooseHowToCalculateDemandForVesselsWithIncompleteHistoricalData'
                ),
            };
        case DemandDefinitionSubstep.DEACTIVATED_VESSELS:
            return {
                title: translate('demandDefinition.deactivatedVessels'),
                description: translate('demandDefinition.chooseWhetherToIncludeHistoricalDemandFromDeactivatedVessels'),
            };
        case DemandDefinitionSubstep.HISTORICAL_DEMAND:
            return {
                title: translate('demandDefinition.historicalDemand'),
                description: translate('demandDefinition.historicalDemandReviewDescription'),
            };
        case DemandDefinitionSubstep.FLEET_CHANGES:
            return {
                title: translate('demandDefinition.fleetChanges'),
                description: translate('demandDefinition.fleetChangesDescription'),
            };
        case DemandDefinitionSubstep.PRODUCT_CHANGES:
            return { title: translate('demandDefinition.productChanges'), description: '' };
        case DemandDefinitionSubstep.DEMAND_ADJUSTMENTS:
            return { title: translate('demandDefinition.demandAdjustments'), description: '' };
        case DemandDefinitionSubstep.TOTAL_VOLUMES:
            return { title: translate('demandDefinition.totalVolumes'), description: '' };
        case DemandDefinitionSubstep.PORT_ALLOCATION:
            return { title: translate('demandDefinition.portAllocation'), description: '' };
        case DemandDefinitionSubstep.SUMMARY:
            return { title: translate('demandDefinition.summary'), description: '' };
    }
};

interface DemandDefinitionFlowProps {
    definitionId?: string;
    dataBasis?: DemandDataBasis;
    dateFrozen?: Date;
    initialDemandDefinition: DemandDefinitionState;
    onClose: (customerGroupId?: string) => void;
    overlayTitle: string;
    substep: string;
}

const validateDemandDefinition = (
    demandDefinition: DemandDefinitionState,
    isSuperAdmin: boolean,
    calculation?: DemandCalculation
): FormikErrors<DemandDefinitionState> => {
    const errors: FormikErrors<DemandDefinitionState> = {};
    const requiredFieldError = translate('errormessage.empty');

    if (demandDefinition.name.trim().length === 0) {
        errors.name = requiredFieldError;
    }

    if (!demandDefinition.referencePeriodFrom) {
        errors.referencePeriodFrom = requiredFieldError;
    }

    if (!demandDefinition.referencePeriodTo) {
        errors.referencePeriodTo = requiredFieldError;
    }

    if (
        demandDefinition.referencePeriodFrom &&
        demandDefinition.referencePeriodTo &&
        differenceInCalendarDays(demandDefinition.referencePeriodFrom, demandDefinition.referencePeriodTo) > 0
    ) {
        errors.referencePeriodTo = translate('demandDefinition.endDateMustBeOnOrAfterStartDate');
    }

    if (isSuperAdmin && !demandDefinition.customerGroupId) {
        errors.customerGroupId = requiredFieldError;
    }

    const fleetChangeErrors = demandDefinition.fleetChanges.map((change) => ({
        mirrorVesselId: getFleetChangeError(change, calculation),
    }));
    if (fleetChangeErrors.some((error) => error.mirrorVesselId)) {
        errors.fleetChanges = fleetChangeErrors;
    }

    return errors;
};

export const DemandDefinitionFlow = ({
    definitionId,
    dataBasis = DemandDataBasis.DYNAMIC,
    dateFrozen,
    initialDemandDefinition,
    onClose,
    overlayTitle,
    substep,
}: DemandDefinitionFlowProps) => {
    const role = useRole();
    const isSuperAdmin = role.isSuperAdmin();
    const isFrozen = dataBasis === DemandDataBasis.STATIC;
    const [isSwitchingDataBasis, setIsSwitchingDataBasis] = useState(false);
    const formik = useFormik<DemandDefinitionState>({
        initialValues: initialDemandDefinition,
        validate: (demandDefinition) =>
            validateDemandDefinition(demandDefinition, isSuperAdmin, calculationQuery.retainedData),
        validateOnMount: true,
        onSubmit: async (demandDefinition) => {
            if (isFleetChangesSaveDisabled) {
                return;
            }
            await submitDemandDefinition(demandDefinition, nextSubstep ? DraftSaveIntent.STAY_IN_FLOW : null);
        },
    });
    const demandDefinition = formik.values;
    const hasReferencePeriod = Boolean(
        demandDefinition.referencePeriodFrom &&
            demandDefinition.referencePeriodTo &&
            differenceInCalendarDays(demandDefinition.referencePeriodFrom, demandDefinition.referencePeriodTo) <= 0
    );
    const hasRequiredCustomerGroup = !isSuperAdmin || demandDefinition.customerGroupId !== undefined;
    const fleetQuery = useQuery({
        ...queries.demandDefinitions.fleet({ customerGroupId: demandDefinition.customerGroupId, definitionId }),
        enabled: hasRequiredCustomerGroup,
    });
    const companies = fleetQuery.data ?? [];
    const categories: DemandProductCategoryOption[] = [
        { id: DemandProductCategory.CYLINDER_OIL, label: translate('demandDefinition.cylinderOil') },
        {
            id: DemandProductCategory.SYSMTEM_OIL_2_STROKE,
            label: translate('demandDefinition.twoStrokeSystemOil'),
        },
        {
            id: DemandProductCategory.ENGINE_OIL_4_STROKE,
            label: translate('demandDefinition.fourStrokeEngineOil'),
        },
        { id: DemandProductCategory.ANCILLARY_OIL, label: translate('demandDefinition.ancillaryOil') },
        { id: DemandProductCategory.GREASE, label: translate('demandDefinition.grease') },
        {
            id: DemandProductCategory.BLEND_ON_BOARD_ADDITIVES,
            label: translate('demandDefinition.blendOnBoardAdditives'),
        },
    ];
    const { activeSubstep, navigateToDefinition, navigateToSubstep, nextSubstep, progressSelection, progressSteps } =
        useDemandDefinitionProgress({
            definitionId,
            hasReferencePeriod,
            hasRequiredCustomerGroup,
            substep,
        });
    const calculationQuery = useDemandCalculation(
        demandDefinition,
        hasReferencePeriod && hasRequiredCustomerGroup,
        demandDefinition.customerGroupId,
        definitionId,
        activeSubstep === DemandDefinitionSubstep.FLEET_CHANGES
    );
    const summaryCalculationQuery = useDemandCalculation(
        demandDefinition,
        hasReferencePeriod && hasRequiredCustomerGroup,
        demandDefinition.customerGroupId,
        definitionId
    );
    const isSummaryCalculationLoading = summaryCalculationQuery.isFetching && !summaryCalculationQuery.data;
    const [companyVolumeCache, setCompanyVolumeCache] = useState<Record<string, Record<string, number>>>({});
    const [isCompanyVolumeCacheFrozen, setIsCompanyVolumeCacheFrozen] = useState(isFrozen);
    if (isCompanyVolumeCacheFrozen !== isFrozen) {
        setIsCompanyVolumeCacheFrozen(isFrozen);
        setCompanyVolumeCache({});
    }
    const historicalVolumeKey = JSON.stringify([
        demandDefinition.customerGroupId,
        demandDefinition.referencePeriodFrom,
        demandDefinition.referencePeriodTo,
    ]);
    useEffect(() => {
        const companyVolumes = calculationQuery.data?.companyVolumes;
        if (!companyVolumes || calculationQuery.isFetching) {
            return;
        }

        setCompanyVolumeCache((cache) => {
            if (cache[historicalVolumeKey]) {
                return cache;
            }

            return { ...cache, [historicalVolumeKey]: companyVolumes };
        });
    }, [calculationQuery.data, calculationQuery.isFetching, historicalVolumeKey]);
    const { exportToExcel, isExporting } = useDemandCalculationExport(demandDefinition.customerGroupId, definitionId);
    const exportCalculation = () =>
        exportToExcel(
            calculationQuery.input,
            `${demandDefinition.name.trim() || translate('demandDefinition.demandDefinition')}.xlsx`
        );
    const createMutation = useCreateDemandDefinition(demandDefinition.customerGroupId);
    const updateMutation = useUpdateDemandDefinition(demandDefinition.customerGroupId);
    const saveMutation = definitionId ? updateMutation : createMutation;
    const patchMutation = usePatchDemandDefinition(demandDefinition.customerGroupId);
    const [isRetryingCalculation, setIsRetryingCalculation] = useState(false);
    const [dismissedCalculationErrorAt, setDismissedCalculationErrorAt] = useState<number>();
    const [draftSaveIntent, setDraftSaveIntent] = useState<DraftSaveIntent | null>(null);
    const [isRetryingDraftSave, setIsRetryingDraftSave] = useState(false);
    const isSavingDraft = draftSaveIntent !== null && saveMutation.isPending;
    const isDraftSaveError = draftSaveIntent !== null && (saveMutation.isError || isRetryingDraftSave);
    const isBusy = saveMutation.isPending || formik.isSubmitting || isRetryingCalculation || isSwitchingDataBasis;
    const navigationGuard = useDemandDefinitionNavigationGuard({
        definitionId,
        hasUnsavedChanges: formik.dirty,
        isBusy,
    });
    const { addErrorToast, addLoadingToast, addToast } = useToasts();
    const companyVolumes = companyVolumeCache[historicalVolumeKey] ?? calculationQuery.data?.companyVolumes;
    const calculatedScope = getCalculatedDemandScope(calculationQuery.data, companies, categories, companyVolumes);
    const showHistoricalData = hasReferencePeriod && hasRequiredCustomerGroup;
    const isCalculationLoading = showHistoricalData && calculationQuery.isFetching;
    const isCompanyVolumeLoading = isCalculationLoading && companyVolumes === undefined;
    const isScopePending = fleetQuery.isPending;
    const isScopeError = fleetQuery.isError;
    const calculationContextValue = {
        calculation: calculationQuery.data,
        retainedCalculation: calculationQuery.retainedData,
        retryCalculation: () => calculationQuery.refetch(),
        categories: calculatedScope.categories,
        companies: calculatedScope.companies,
        isCalculationError: calculationQuery.isError,
        isCalculationFetching: calculationQuery.isFetching,
        isCalculationLoading,
        isCompanyVolumeLoading,
        isCalculationPending: calculationQuery.isPending,
        showHistoricalData,
    };

    const { validateForm } = formik;
    useEffect(() => {
        validateForm();
    }, [calculationQuery.retainedData, validateForm]);

    const isFleetChangesSubstep =
        activeSubstep === DemandDefinitionSubstep.NEWLY_ADDED_VESSELS ||
        activeSubstep === DemandDefinitionSubstep.DEACTIVATED_VESSELS;
    const isFleetChangesSaveDisabled =
        (isFleetChangesSubstep ||
            demandDefinition.fleetChanges.length > 0 ||
            demandDefinition.leavingVessels.length > 0) &&
        (calculationQuery.isFetching || calculationQuery.isError || !calculationQuery.data);

    const closeAfterSave = (customerGroupId: string) => {
        navigationGuard.allowNavigation();
        onClose(customerGroupId);
    };

    const persistDemandDefinition = (values: DemandDefinitionState): Promise<DemandDefinition> => {
        const saveRequest = {
            name: values.name.trim(),
            reference: values.reference.trim() || undefined,
            internalNotes: values.internalNotes.trim() || undefined,
            input: toDemandDefinitionRequestInput(values),
        };
        return definitionId
            ? updateMutation.mutateAsync({ definitionId, request: saveRequest })
            : createMutation.mutateAsync(saveRequest);
    };

    const submitDemandDefinition = async (values: DemandDefinitionState, saveIntent: DraftSaveIntent | null) => {
        setDraftSaveIntent(saveIntent);
        try {
            const definition = await persistDemandDefinition(values);

            formik.resetForm({ values });

            if (saveIntent === DraftSaveIntent.CLOSE_FLOW) {
                addToast(translate('demandDefinition.draftSaved'));
                closeAfterSave(definition.groupId);
                return;
            }

            if (saveIntent === DraftSaveIntent.STAY_IN_FLOW) {
                addToast(translate('demandDefinition.draftSaved'));
                setDraftSaveIntent(null);

                if (!definitionId) {
                    navigationGuard.allowNavigation();
                    navigateToDefinition(definition.id, activeSubstep);
                }

                return;
            }

            addToast(translate('demandDefinition.demandDefinitionSaved'));
            closeAfterSave(definition.groupId);
        } catch {
            if (saveIntent === null) {
                addErrorToast(translate('demandDefinition.demandDefinitionCouldNotBeSaved'));
            }
        }
    };

    const makeDataStatic = async () => {
        let createdDefinitionId: string | undefined;
        await addLoadingToast(
            async () => {
                const definition = await persistDemandDefinition(demandDefinition);
                formik.resetForm({ values: demandDefinition });
                createdDefinitionId = definitionId ? undefined : definition.id;
                await patchMutation.mutateAsync({
                    definitionId: definition.id,
                    request: { dataBasis: DemandDataBasis.STATIC },
                });
            },
            translate('demandDefinition.makingDataStatic'),
            translate('demandDefinition.dataStatic'),
            translate('demandDefinition.dataCouldNotBeMadeStatic')
        ).catch(() => undefined);

        if (createdDefinitionId) {
            navigationGuard.allowNavigation();
            navigateToDefinition(createdDefinitionId, activeSubstep);
        }
    };

    const makeDataDynamic = async () => {
        if (!definitionId) {
            return;
        }

        await addLoadingToast(
            () => patchMutation.mutateAsync({ definitionId, request: { dataBasis: DemandDataBasis.DYNAMIC } }),
            translate('demandDefinition.makingDataDynamic'),
            translate('demandDefinition.dataDynamic'),
            translate('demandDefinition.dataCouldNotBeMadeDynamic')
        ).catch(() => undefined);
    };

    const changeDataBasis = async (nextDataBasis: DemandDataBasis) => {
        setIsSwitchingDataBasis(true);
        await (nextDataBasis === DemandDataBasis.STATIC ? makeDataStatic() : makeDataDynamic());
        setIsSwitchingDataBasis(false);
    };

    const retryDraftSave = async () => {
        setIsRetryingDraftSave(true);
        try {
            await submitDemandDefinition(demandDefinition, draftSaveIntent);
        } finally {
            setIsRetryingDraftSave(false);
        }
    };

    const dismissDraftSaveError = () => {
        saveMutation.reset();
        setDraftSaveIntent(null);
    };

    const retryCalculation = async () => {
        setIsRetryingCalculation(true);
        try {
            await calculationQuery.refetch();
        } finally {
            setIsRetryingCalculation(false);
        }
    };

    const renderStep = () => {
        switch (activeSubstep) {
            case DemandDefinitionSubstep.GENERAL:
                return (
                    <>
                        {isSuperAdmin ? <DemandDefinitionCustomerGroupField definitionId={definitionId} /> : null}
                        <GeneralStep />
                    </>
                );
            case DemandDefinitionSubstep.REFERENCE_PERIOD:
                return <ReferencePeriodStep isLocked={isFrozen} />;
            case DemandDefinitionSubstep.FLEET_SCOPE:
                if (isScopePending) {
                    return <LoadingIndicator variant="full-height" />;
                }
                if (isScopeError) {
                    return <AdvancedError variant="fluid" />;
                }
                return <FleetScopeStep />;
            case DemandDefinitionSubstep.PRODUCT_CATEGORIES:
                return <ProductCategoriesStep />;
            case DemandDefinitionSubstep.HISTORICAL_DEMAND:
                return (
                    <Box display="flex" flexDirection="column" gap={24}>
                        <HistoricalDemandStep />
                        <DemandDataFreezeControl
                            dataBasis={dataBasis}
                            dateFrozen={dateFrozen}
                            isDisabled={formik.isSubmitting}
                            isStaticDisabled={!formik.isValid || isFleetChangesSaveDisabled}
                            onDataBasisChange={changeDataBasis}
                        />
                    </Box>
                );
            case DemandDefinitionSubstep.NEWLY_ADDED_VESSELS:
                if (isScopePending) {
                    return <LoadingIndicator variant="full-height" />;
                }
                if (isScopeError) {
                    return <AdvancedError variant="fluid" />;
                }
                return <FleetChangesStep key={activeSubstep} isDeactivated={false} />;
            case DemandDefinitionSubstep.DEACTIVATED_VESSELS:
                if (isScopePending) {
                    return <LoadingIndicator variant="full-height" />;
                }
                if (isScopeError) {
                    return <AdvancedError variant="fluid" />;
                }
                return <FleetChangesStep key={activeSubstep} isDeactivated />;
            case DemandDefinitionSubstep.FLEET_CHANGES:
                if (isScopePending) {
                    return <LoadingIndicator variant="full-height" />;
                }
                if (isScopeError) {
                    return <AdvancedError variant="fluid" />;
                }
                return <ExpectedFleetChangesStep companies={companies} />;
            case DemandDefinitionSubstep.PRODUCT_CHANGES:
            case DemandDefinitionSubstep.DEMAND_ADJUSTMENTS:
            case DemandDefinitionSubstep.TOTAL_VOLUMES:
            case DemandDefinitionSubstep.PORT_ALLOCATION:
            case DemandDefinitionSubstep.SUMMARY:
                return null;
        }
    };

    const stepCopy = getSubstepCopy(activeSubstep);
    const isNextDisabled =
        !hasRequiredCustomerGroup ||
        (activeSubstep === DemandDefinitionSubstep.REFERENCE_PERIOD && !hasReferencePeriod) ||
        (!hasReferencePeriod &&
            (nextSubstep === DemandDefinitionSubstep.HISTORICAL_DEMAND ||
                nextSubstep === DemandDefinitionSubstep.NEWLY_ADDED_VESSELS));
    const isCalculationErrorDismissed = calculationQuery.errorUpdatedAt === dismissedCalculationErrorAt;
    const hasCalculationError =
        draftSaveIntent !== DraftSaveIntent.CLOSE_FLOW &&
        showHistoricalData &&
        ((calculationQuery.isError && !isCalculationErrorDismissed) || isRetryingCalculation);
    let activeOverlay: DemandFlowOverlay | null = null;
    if (isDraftSaveError) {
        activeOverlay = DemandFlowOverlay.DRAFT_SAVE_ERROR;
    } else if (!isSavingDraft && hasCalculationError) {
        activeOverlay = DemandFlowOverlay.CALCULATION_ERROR;
    }
    const isContentInteractionBlocked = activeOverlay !== null;
    const overlayTransitions = useTransition(activeOverlay, {
        from: { opacity: 0 },
        enter: { opacity: 1 },
        leave: { opacity: 0 },
        config: { duration: 150 },
    });

    const requestClose = () => onClose(definitionId ? demandDefinition.customerGroupId : undefined);

    return (
        <FormikProvider value={formik}>
            <DemandDefinitionCalculationContext.Provider value={calculationContextValue}>
                <DemandDefinitionFlowLayout
                    title={overlayTitle}
                    onClose={requestClose}
                    isCloseDisabled={isBusy}
                    isContentInteractionBlocked={isContentInteractionBlocked}
                    isBusy={isBusy}
                    areActionsHidden={activeOverlay !== null}
                    actions={
                        <DemandDefinitionFlowActions
                            isSavingDraft={isSavingDraft}
                            isExportDisabled={!calculationQuery.data}
                            isExporting={isExporting}
                            isNextDisabled={isNextDisabled || isSwitchingDataBasis}
                            isSaveDisabled={isFleetChangesSaveDisabled || isSwitchingDataBasis}
                            nextSubstep={nextSubstep}
                            onExport={exportCalculation}
                            onNext={navigateToSubstep}
                        />
                    }
                    navigation={
                        <ProgressSteps
                            steps={progressSteps}
                            selection={progressSelection}
                            onSelect={({ substepId }) => navigateToSubstep(substepId)}
                        />
                    }
                    panelContent={
                        <DemandDefinitionSummaryPanel
                            step={progressSelection.stepId}
                            calculation={
                                isSummaryCalculationLoading
                                    ? summaryCalculationQuery.retainedData
                                    : summaryCalculationQuery.data
                            }
                            isLoading={isSummaryCalculationLoading}
                            isError={summaryCalculationQuery.isError}
                        />
                    }
                    contentOverlay={
                        <>
                            {overlayTransitions((styles, overlay) => {
                                if (!overlay) {
                                    return null;
                                }

                                let content: ReactNode;
                                if (overlay === DemandFlowOverlay.CALCULATION_ERROR) {
                                    content = (
                                        <DemandCalculationErrorOverlay
                                            canSaveDraft={formik.isValid}
                                            isRetrying={isRetryingCalculation}
                                            onRetry={retryCalculation}
                                            onSaveDraft={() =>
                                                submitDemandDefinition(demandDefinition, DraftSaveIntent.CLOSE_FLOW)
                                            }
                                            onKeepEditing={() =>
                                                setDismissedCalculationErrorAt(calculationQuery.errorUpdatedAt)
                                            }
                                            onClose={requestClose}
                                        />
                                    );
                                } else {
                                    content = (
                                        <DemandDraftSaveErrorOverlay
                                            isRetrying={isRetryingDraftSave}
                                            onRetry={retryDraftSave}
                                            onKeepEditing={dismissDraftSaveError}
                                            onClose={requestClose}
                                        />
                                    );
                                }

                                return (
                                    <animated.div
                                        style={{ ...styles, position: 'absolute', inset: 0 }}
                                        inert={overlay !== activeOverlay}
                                    >
                                        {content}
                                    </animated.div>
                                );
                            })}
                        </>
                    }
                    panelOverlay={
                        <ContentLoadingOverlay
                            isLoading={isSummaryCalculationLoading}
                            label={translate('demandDefinition.recalculating')}
                        />
                    }
                >
                    <StepContent $stretchContent={isFleetChangesSubstep}>
                        <Box display="flex" flexDirection="column" gap={4} marginBottom={24}>
                            <Text as="h1" variant="title">
                                {stepCopy.title}
                            </Text>
                            {stepCopy.description ? (
                                <Text variant="small" color="foreground.muted">
                                    {stepCopy.description}
                                </Text>
                            ) : null}
                        </Box>

                        {formik.errors.fleetChanges && activeSubstep !== DemandDefinitionSubstep.NEWLY_ADDED_VESSELS ? (
                            <Box marginBottom={24}>
                                <Callout
                                    variant="negative"
                                    description={translate(
                                        'demandDefinition.reviewNewlyAddedVesselSelectionsBeforeSaving'
                                    )}
                                >
                                    <Button
                                        onClick={() => navigateToSubstep(DemandDefinitionSubstep.NEWLY_ADDED_VESSELS)}
                                    >
                                        {translate('demandDefinition.addedVessels')}
                                    </Button>
                                </Callout>
                            </Box>
                        ) : null}
                        {renderStep()}
                    </StepContent>
                </DemandDefinitionFlowLayout>
            </DemandDefinitionCalculationContext.Provider>
        </FormikProvider>
    );
};
